import { datadogRum } from '@datadog/browser-rum';
import { hashUserId } from 'shared-components/utils/hashUserId';

export type DatadogRumCustomActionContext = Record<string, unknown>;

const CONTEXT_KEYS_TO_HASH: readonly string[] = ['patientId'];

const hashContextValueIfNeeded = async (key: string, value: unknown): Promise<unknown> => {
  if (!CONTEXT_KEYS_TO_HASH.includes(key)) {
    return value;
  }
  if (value === undefined || value === null) {
    return value;
  }
  const raw = typeof value === 'string' ? value : typeof value === 'number' ? String(value) : null;
  if (raw === null) {
    return value;
  }
  return hashUserId(raw);
};

const sanitizeContext = async (
  context: DatadogRumCustomActionContext | undefined,
): Promise<DatadogRumCustomActionContext> => {
  if (!context) {
    return {};
  }
  const entries = await Promise.all(
    Object.entries(context).map(async ([key, value]) => [key, await hashContextValueIfNeeded(key, value)] as const),
  );
  return Object.fromEntries(entries);
};

/**
 * Records a Datadog Browser RUM custom action (see `addAction`).
 */
export const logDatadogRumCustomAction = (actionName: string, context?: DatadogRumCustomActionContext): void => {
  const send = async () => {
    try {
      const sanitized = await sanitizeContext(context);
      const payload: DatadogRumCustomActionContext = {
        ...sanitized,
        clientTimestamp: new Date().toISOString(),
      };
      datadogRum.addAction(actionName, payload);
    } catch {
      // RUM is best-effort; avoid surfacing hashing/addAction failures to the user
    }
  };

  if (datadogRum.getInitConfiguration()) {
    void send();
    return;
  }

  datadogRum.onReady(() => void send());
};
